export interface LoopCallbacks {
  update: (dt: number) => void;
  render: () => void;
}

const FIXED_DT = 1 / 60;
const MAX_FRAME_DT = 0.25; // clamp huge pauses (tab backgrounded, breakpoint, etc.)

/**
 * Fixed-timestep update, once-per-frame render. Every `update(dt)` call gets
 * an identical dt (1/60) so anything built on top (movement, AI, replay) can
 * assume deterministic steps — same discipline as Classic's `update(1/60)`.
 */
export class GameLoop {
  private rafHandle = 0;
  private lastTime = 0;
  private accumulator = 0;
  private running = false;

  constructor(private readonly callbacks: LoopCallbacks) {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.rafHandle = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };

  private tick = (now: number): void => {
    if (!this.running) return;
    const frameDt = Math.min((now - this.lastTime) / 1000, MAX_FRAME_DT);
    this.lastTime = now;
    this.accumulator += frameDt;
    while (this.accumulator >= FIXED_DT) {
      this.callbacks.update(FIXED_DT);
      this.accumulator -= FIXED_DT;
    }
    this.callbacks.render();
    // Re-check `running` — a stop() called from inside update()/render() itself
    // (e.g. a future pause menu) must not leave a stray frame queued, which
    // would double-step updates on the next start().
    if (this.running) {
      this.rafHandle = requestAnimationFrame(this.tick);
    }
  };
}
